

// StorageManagement.js: Admin page for managing storage uploads and file locations

import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, IconButton, Grid, Breadcrumbs, Link, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FolderIcon from '@mui/icons-material/Folder';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import StorageUploadModal from '../../components/StorageUploadModal';
import FileThumbnail from '../../components/FileThumbnail';


function StorageManagement() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [moveAnchor, setMoveAnchor] = useState(null);
  const [moveFile, setMoveFile] = useState(null);
  const [foldersFlat, setFoldersFlat] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Fetch folders and files for current folder
  const fetchData = async (folderId = null, updateBreadcrumbs = true) => {
    setLoading(true);
    try {
      const [foldersRes, filesRes] = await Promise.all([
        api.get('/api/folders', { params: { parent: folderId } }),
        api.get('/api/storage', { params: { parent: folderId } })
      ]);
      setFolders(foldersRes.data);
      setFiles(filesRes.data);
      setCurrentFolder(folderId);
      if (updateBreadcrumbs) {
        // Fetch breadcrumbs
        let crumbs = [];
        let fid = folderId;
        while (fid) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get(`/api/folders`, { params: { _id: fid } });
          if (data && data[0]) {
            crumbs.unshift(data[0]);
            fid = data[0].parent;
          } else {
            break;
          }
        }
        setBreadcrumbs(crumbs);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch storage', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all folders flat for move dialog
  const fetchFoldersFlat = async () => {
    const { data } = await api.get('/api/folders');
    setFoldersFlat(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Upload handler for modal
  const handleUpload = async (file, setProgress, setError) => {
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) formData.append('parent', currentFolder);
    setLoading(true);
    try {
      const res = await api.post('/api/storage/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / e.total))
      });
      setFiles(prev => [...prev, res.data]);
      setSnackbar({ open: true, message: 'File uploaded successfully', severity: 'success' });
      setUploadOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  // Delete file
  const handleDelete = async (id) => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/storage/${id}`);
      setFiles(prev => prev.filter(f => f._id !== id));
      setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Move file
  const handleMove = async (file, folderId) => {
    setLoading(true);
    try {
      await api.patch(`/api/storage/${file._id}/move`, { newParent: folderId });
      setFiles(prev => prev.filter(f => f._id !== file._id));
      setSnackbar({ open: true, message: 'File moved successfully', severity: 'success' });
      setMoveAnchor(null);
      setMoveFile(null);
      fetchData(currentFolder, false);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Move failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Download file
  const handleDownload = (file) => {
    window.open(file.url, '_blank');
  };

  // Preview file
  const handlePreview = (file) => {
    setPreviewFile(file);
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setLoading(true);
    try {
      await api.post('/api/folders', { name: newFolderName, parent: currentFolder });
      setSnackbar({ open: true, message: 'Folder created', severity: 'success' });
      setCreateFolderOpen(false);
      setNewFolderName('');
      fetchData(currentFolder);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to create folder', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <SuperAdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" fontWeight="bold" mb={2} color="primary">Storage Management</Typography>
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
              onClick={() => setUploadOpen(true)}
            >
              Upload File
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
              onClick={() => setCreateFolderOpen(true)}
            >
              New Folder
            </Button>
            <Typography variant="body2" color="text.secondary">Manage all uploaded files and folders.</Typography>
          </Box>
          <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
            <Link underline="hover" color="inherit" onClick={() => fetchData(null)} sx={{ cursor: 'pointer' }}>Root</Link>
            {breadcrumbs.map((crumb, idx) => (
              <Link key={crumb._id} underline="hover" color="inherit" onClick={() => fetchData(crumb._id)} sx={{ cursor: 'pointer' }}>{crumb.name}</Link>
            ))}
          </Breadcrumbs>
          <NotificationSnackbar
            open={snackbar.open}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            message={snackbar.message}
            severity={snackbar.severity}
          />
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Grid container spacing={2}>
              {/* Folders */}
              {folders.map(folder => (
                <Grid item xs={6} sm={4} md={3} key={folder._id}>
                  <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2, boxShadow: 1 }} onClick={() => fetchData(folder._id)}>
                    <FolderIcon color="primary" sx={{ fontSize: 48 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>{folder.name}</Typography>
                  </Paper>
                </Grid>
              ))}
              {/* Files */}
              {files.map(file => (
                <Grid item xs={6} sm={4} md={3} key={file._id}>
                  <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, boxShadow: 1, position: 'relative' }}>
                    <Box onClick={() => handlePreview(file)} sx={{ cursor: 'pointer' }}>
                      <FileThumbnail file={file} size={56} />
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 1, wordBreak: 'break-all' }}>{file.fileName}</Typography>
                      <Typography variant="caption" color="text.secondary">{file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : '-'}</Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => handleDownload(file)}><DownloadIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="info" onClick={e => { setMoveAnchor(e.currentTarget); setMoveFile(file); fetchFoldersFlat(); }}><DriveFileMoveIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(file._id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
        {/* Upload Modal */}
        <StorageUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUpload={handleUpload} />
        {/* Move File Menu */}
        <Menu anchorEl={moveAnchor} open={Boolean(moveAnchor)} onClose={() => setMoveAnchor(null)}>
          {foldersFlat.filter(f => f._id !== (moveFile && moveFile.parent)).map(folder => (
            <MenuItem key={folder._id} onClick={() => handleMove(moveFile, folder._id)}>{folder.name}</MenuItem>
          ))}
        </Menu>
        {/* Preview Dialog */}
        <Dialog open={!!previewFile} onClose={() => setPreviewFile(null)} maxWidth="md">
          <DialogTitle>Preview: {previewFile?.fileName}</DialogTitle>
          <DialogContent>
            {previewFile && previewFile.type && previewFile.type.startsWith('image/') ? (
              <img src={previewFile.url} alt={previewFile.fileName} style={{ maxWidth: '100%', maxHeight: 400 }} />
            ) : (
              <Typography variant="body2">No preview available for this file type.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewFile(null)}>Close</Button>
            <Button onClick={() => handleDownload(previewFile)} variant="contained">Download</Button>
          </DialogActions>
        </Dialog>
        {/* Create Folder Dialog */}
        <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogContent>
            <TextField label="Folder Name" fullWidth value={newFolderName} onChange={e => setNewFolderName(e.target.value)} autoFocus sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default StorageManagement;
